import { FC, useState } from 'react';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import {
  useEventCritiqueDataQuery,
  useEventInterestDataQuery,
  useEventQuery,
} from '@queries/EventQueries';
import { FeedPost, useEventCommentsQuery } from '@queries/PostQueries';
import { useUserConnectionComparisonQuery } from '@queries/UserConnectionsQueries';
import { useCurrentUser } from '@queries/UserQueries';

import {
  CritiqueDocumentMutationPayload,
  useCritiqueDocumentMutation,
} from '@mutations/CritiqueMutations';
import { useCreateEventComment } from '@mutations/PostMutations';

import { useTipEntertainer } from '@hooks/EntertainerHooks/useTipEntertainer';
import { useIsPastEvent } from '@hooks/EventHooks/useIsPastEvent';
import { useAnalytics } from '@hooks/useAnalytics';
import { useReferredBy } from '@hooks/useReferredBy';
import { useShareLink } from '@hooks/useShareLink';

import { useLocation, useNavigate, useParams } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { getEntertainerSeoLink } from '@utilities/entertainerUtilities/getEntertainerSeoLink';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { DynamicActionButton } from '@components/buttons/DynamicActionButton';
import IconButton from '@components/buttons/IconButton';
import { SendEntertainerBadgeButton } from '@components/buttons/SendEntertainerBadgeButton';
import { EventActions, EventHeader, EventImage, PromoterHeader } from '@components/cards/EventCard';
import { PostCard } from '@components/cards/PostCard/PostCard';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import Card from '@components/layout/Card/Card';
import { CritiqueSection } from '@components/layout/DetailsChartSection/CritiqueSection';
import { EventInterestSection } from '@components/layout/DetailsChartSection/EventInterestSection';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Screen } from '@components/layout/Screen/Screen';
import { SeoLink } from '@components/layout/SeoLink/SeoLink';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { UserAvatar } from '@components/layout/UserAvatar/UserAvatar';
import { PrimaryCritiqueCountChips } from '@components/overlays/CritiqueOverlay/PrimaryCritiqueCountChips';
import { ReportOverlay } from '@components/overlays/ReportOverlay/ReportOverlay';

import { VenmoLogo } from '../../../assets/VenmoLogo';
import fireIconUrl from '../../CommunityScreen/assets/fire.svg';
import { useJoinLineup } from '../../CommunityScreen/components/ShowsTabContent';
import { EventCleanupPopup } from '../../EventCleanupScreen';
import { useAuthGate } from '../../LogInScreen/AuthGateContext';
import { CastReferralPopup } from '../EventScreen/CastReferralPopup';
import { EntertainersSection } from '../EventScreen/EventReviewsScreen';
import { EventConnectPopup } from './EventConnectPopup';
import { EventInvitationPopup } from './EventInvitationPopup';
import { EventInvitePopup } from './EventInvitePopup';
import { EventRsvpsSection } from './components/EventRsvpsSection';
import { useEventRsvpActions } from './hooks/useEventRsvpActions';

const EventCommentSection: FC<{
  eventId: string;
  commentFieldIsVisible: boolean;
  setCommentFieldIsVisible: (updatedValue: boolean) => void;
}> = ({ eventId, commentFieldIsVisible, setCommentFieldIsVisible }) => {
  const [commentText, setCommentText] = useState<string>('');

  const { createAuthGateHandler } = useAuthGate();
  const { eventComments = [], eventCommentsAreReady } = useEventCommentsQuery(eventId);
  const { isSubmitting, createEventCommentAsync } = useCreateEventComment(eventId);

  const handleSubmitComment = async () => {
    if (!commentText || isSubmitting) {
      return;
    }

    createAuthGateHandler(async () => {
      await createEventCommentAsync({ text: commentText });

      setCommentText('');
    });
  };

  return (
    <>
      <div id="event-comments" />
      <TitleToolbar
        renderAs="h3"
        size="md"
        text="Comments"
        action={
          <Button disabled={false} variant="flat" onClick={() => setCommentFieldIsVisible(true)}>
            Add Comment
          </Button>
        }
      />
      {commentFieldIsVisible && (
        <Textarea
          onChange={setCommentText}
          submitButton={{
            text: 'Comment',
            disabled: isSubmitting,
            onClick: handleSubmitComment,
          }}
        />
      )}

      {eventCommentsAreReady && (
        <>
          {eventComments.length ? (
            <div className="comments">
              {eventComments.map((comment) => (
                <PostCard key={comment._id} post={comment} showDetails={false} />
              ))}
            </div>
          ) : (
            <EmptyState
              className="comments-empty-state"
              title="No Comments Yet"
              text="Be the first to comment or ask a question about this event."
              button={{
                text: 'Add Comment',
                onClick: () => setCommentFieldIsVisible(true),
              }}
            />
          )}
        </>
      )}
    </>
  );
};

interface EventScreenProps {
  back?: string;
}

export const TipEntertainerButton: FC<{ entertainer: any }> = ({ entertainer }) => {
  const { handleVenmoEntertainer, tipEntertainerPopup } = useTipEntertainer();
  const handleTip = () => {
    handleVenmoEntertainer({
      entertainerId: entertainer._id,
      venmo: entertainer?.contact?.venmo,
    });
  };

  return (
    <>
      <Button
        size="sm"
        roundness="rounded"
        style={{
          borderRadius: 4,
        }}
        startIcon={
          <div style={{ width: 14, height: 14 }}>
            <VenmoLogo />
          </div>
        }
        onClick={handleTip}
      >
        Tip
      </Button>
      {tipEntertainerPopup}
    </>
  );
};

const IS_CONFIRMATION_ENABLED = false;

export const UnconfirmedEntertainerButton: FC<{
  entertainer: { _id: string; title: string; contact?: { instagram?: string } };
  eventId: string;
  onJoinLineup: () => void;
}> = ({ eventId, entertainer, onJoinLineup }) => {
  const url = 'https://ig.me/m/gaggl.app';

  const { openAlertDialog, openConfirmDialog } = useDialog();

  const code = '0000';

  const handleConfirm = () => {
    if (IS_CONFIRMATION_ENABLED) {
      openConfirmDialog({
        title: 'Is this you?',
        text: `Only ${entertainer.title} can confirm themselves in the cast list. If you are ${entertainer.title}, please confirm your account by clicking the button below.`,
        onConfirm: async () => {
          // await copyTextToClipboard(code);
          openAlertDialog({
            title: 'Confirm with Instagram',
            text: `We copied the code "${code}" to your clipboard. Use the button below to open a message with us at @gaggl.app and send the code to confirm your account.`,
            cb: () => {
              openExternalUrl(url);
            },
          });
        },
        buttonOk: 'Confirm',
        buttonCancel: 'Not me',
      });
    } else {
      onJoinLineup();
    }
  };

  if (!entertainer.contact?.instagram) {
    return <></>;
  }

  return (
    <Button
      size="sm"
      roundness="rounded"
      color="danger"
      variant="smooth"
      style={{
        borderRadius: 4,
      }}
      startIcon={<FontAwesomeIcon name="info-circle" />}
      onClick={handleConfirm}
    >
      Unconfirmed
    </Button>
  );
};

const ShareEventButton = ({ event }: { event }) => {
  const { didCopy, shareLink } = useShareLink();

  const handleShare = () => {
    shareLink({
      title: event.title,
      url: `https://my.gaggl.app/events/${event._id}`,
    });
  };

  return (
    <Button
      roundness="rounded"
      variant="smooth"
      color="neutral"
      startIcon={<FontAwesomeIcon name="share" />}
      onClick={handleShare}
    >
      {didCopy ? 'Copied!' : 'Share Event'}
    </Button>
  );
};

export const EventRsvpScreen = createScreenComponent<
  { eventId: string },
  { referredBy?: string; referralType?: 'entertainer' | 'default' }
>(({ eventId, back, f7route }) => {
  const { referredBy, referralType = 'default' } = f7route.query ?? {};
  const { track } = useAnalytics();
  const { eventIsReady, event, entertainerEventReferralUrl, refetchEvent, userEventInviteUrl } =
    useEventQuery(eventId);
  const { eventCritiqueData, refetchEventCritiqueData } = useEventCritiqueDataQuery(eventId);
  const { eventInterestData, refetchEventInterestData } = useEventInterestDataQuery(eventId);
  const { currentUser } = useCurrentUser();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const [commentFieldIsVisible, setCommentFieldIsVisible] = useState<boolean>(false);
  const [reportOverlayIsVisible, setReportOverlayIsVisible] = useState(false);
  const [invitationPopupDismissed, setInvitationPopupDismissed] = useState(false);
  const [connectPopupIsVisible, setConnectPopupIsVisible] = useState(false);
  const [invitePopupIsVisible, setInvitePopupIsVisible] = useState(false);
  const [isCastReferralPopupVisible, setIsCastReferralPopupVisible] = useState(false);
  const navigate = useNavigate();
  const { createAuthGateHandler } = useAuthGate();
  const { connectionComparison } = useUserConnectionComparisonQuery(
    referralType === 'entertainer' ? undefined : referredBy,
  );

  const { critiqueDocumentAsync } = useCritiqueDocumentMutation();

  const {
    rsvp,
    isCurrentUserAttending,
    isCurrentUserInterested,
    isUpdatingRsvp,
    handleMarkInterested,
    handleMarkAttending,
  } = useEventRsvpActions({ eventId, eventTitle: event?.title }, referredBy);

  const userDidHype = rsvp?.eventInterest === 'hyped';
  const { isPastEvent } = useIsPastEvent(event);

  return (
    <Screen
      className="event-screen"
      name="Event Screen"
      disableLocationPermissionGate
      headerProps={{
        back: back ? { to: back } : undefined,
        title: 'Event Details',
        right: (
          <IconButton
            disabled={false}
            variant="flat"
            color="danger"
            onClick={() => setReportOverlayIsVisible(true)}
          >
            <FontAwesomeIcon faStyle="far" name="flag" />
          </IconButton>
        ),
      }}
      bottomToolbar={
        !isPastEvent && (
          <Screen.BottomToolbar maxWidth={425}>
            <div className="d-flex gap-2 w-100">
              <div className="w-50">
                <Button
                  noWrap
                  roundness="rounded"
                  disabled={isUpdatingRsvp}
                  variant={isCurrentUserAttending ? 'default' : 'outlined'}
                  startIcon={
                    <FontAwesomeIcon
                      faStyle="far"
                      name={isCurrentUserAttending ? 'check-circle' : 'circle'}
                    />
                  }
                  onClick={handleMarkAttending}
                >
                  Attending
                </Button>
              </div>
              <div className="w-50">
                <Button
                  noWrap
                  roundness="rounded"
                  disabled={isUpdatingRsvp}
                  color="neutral"
                  variant={isCurrentUserInterested ? 'default' : 'outlined'}
                  startIcon={
                    <FontAwesomeIcon
                      faStyle="far"
                      name={isCurrentUserInterested ? 'check-circle' : 'circle'}
                    />
                  }
                  onClick={handleMarkInterested}
                >
                  Interested
                </Button>
              </div>
            </div>
          </Screen.BottomToolbar>
        )
      }
    >
      <Screen.Content>
        {eventIsReady ? (
          event && (
            <>
              {connectionComparison?.connectionName && rsvp?.rsvp !== 'yes' && (
                <Card className="mb-3">
                  <Card.Body>
                    <div className="text-center fs-1">
                      <strong>{`${connectionComparison?.connectionName} wants to know if you're going!`}</strong>
                    </div>
                  </Card.Body>
                </Card>
              )}
              {/* <PromoterHeader event={event} /> */}
              <EventImage event={event} />
              <EventHeader event={event} subtitleDirection="column" hideRsvp />
              {event.externalLinkButton?.url && (
                <Button
                  className="mb-3"
                  roundness="rounded"
                  onClick={() => openExternalUrl(event.externalLinkButton.url)}
                >
                  {event.externalLinkButton.text ?? 'Get Tickets'}
                </Button>
              )}
              {currentUser?.isSuperAdmin && (
                <Button className="mb-3" onClick={() => navigate(`/events/${eventId}/cleanup`)}>
                  Edit Event
                </Button>
              )}
              {referralType === 'entertainer' && (
                <EntertainersSection
                  eventId={eventId}
                  entertainerEventReferralUrl={entertainerEventReferralUrl}
                  onOpenCastReferralPopup={() => setIsCastReferralPopupVisible(true)}
                />
              )}
              <EventRsvpsSection
                eventId={eventId}
                referredBy={referredBy}
                onInvite={() => setInvitePopupIsVisible(true)}
              />
              {(event.excerpt ?? event.description) && (
                <>
                  <TitleToolbar renderAs="h3" size="md" text="About This Event" />
                  <div className="event-description">
                    {mapStringToParagraphs(event.excerpt ?? event.description)}{' '}
                  </div>
                </>
              )}
            </>
          )
        ) : (
          <div className="d-grid gap-1">
            <Skeleton height={200} />
            <Skeleton height={30} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </div>
        )}
      </Screen.Content>
      <EventInvitePopup
        isVisible={invitePopupIsVisible}
        eventId={eventId}
        onClose={() => setInvitePopupIsVisible(false)}
      />
      {connectionComparison && event && (
        <EventInvitationPopup
          isVisible={!!connectionComparison && !invitationPopupDismissed}
          event={event}
          connectionComparison={connectionComparison}
          onClose={() => setInvitationPopupDismissed(true)}
          onSubmit={() => {
            setInvitationPopupDismissed(true);
            if (!connectionComparison._id) {
              setConnectPopupIsVisible(true);
            }
          }}
        />
      )}
      {connectionComparison && (
        <EventConnectPopup
          isVisible={connectPopupIsVisible}
          connectionComparison={connectionComparison}
          onClose={() => setConnectPopupIsVisible(false)}
        />
      )}
      <CastReferralPopup
        isVisible={isCastReferralPopupVisible}
        onClose={() => {
          setIsCastReferralPopupVisible(false);
        }}
        entertainers={event?.entertainers
          ?.filter(
            (entertainer) =>
              entertainer.status === 'pending' && entertainer._id !== currentEntertainer?._id,
          )
          ?.map((entertainer) => ({
            _id: entertainer._id,
            title: entertainer.title,
            contact: entertainer.contact,
          }))}
        referralUrl={entertainerEventReferralUrl}
      />
    </Screen>
  );
});
